/**
 * P17 — the device half of D51's "use current location".
 *
 * **One read, never a stream.** The module exports `readDevicePosition()` and nothing else that
 * touches the API, and it calls `getCurrentPosition` — `watchPosition` is absent on purpose. Q11
 * and the D73 DPIA are both written around *capture-time processing, not tracking*, so a watch
 * here is not an accuracy improvement, it is a different processing activity than the one counsel
 * is being asked to sign off. The same reason rules out a warm-up read at launch: the prompt is
 * owed at the capture step, with a purpose string, and a position captured before the technician
 * asked for one is a position captured for no stated purpose.
 *
 * **Nothing here reads `navigator.permissions`.** A `permissions.query({name:'geolocation'})` would
 * let the caller pre-empt the prompt, and the only thing it could usefully do with the answer is
 * decide whether to show the control — which is exactly the decision §7.8 makes the other way: the
 * control is offered, the tap is the consent, and a refusal is handled once. Safari's support for
 * that query has also been partial for years, so the branch would be untestable on the platform
 * that matters most here.
 */

export type GeolocationFailureReason = 'unsupported' | 'denied' | 'unavailable' | 'timeout'

/** A raw device fix. `accuracyMetres` is for display only — nothing is stored from it. */
export interface DevicePosition {
  readonly lat: number
  readonly lng: number
  readonly accuracyMetres: number
}

export type DevicePositionResult =
  | { readonly ok: true; readonly position: DevicePosition }
  | { readonly ok: false; readonly reason: GeolocationFailureReason }

type GeolocationLike = Pick<Geolocation, 'getCurrentPosition'>

/**
 * 🔸 High accuracy, a 15-second ceiling, and **`maximumAge: 0`**.
 *
 * The last one is the one with a rule behind it rather than a preference: a cached fix is a
 * position captured at some earlier moment for some earlier reason, and D51's whole claim is that
 * the technician is standing at the gate *now*. Accepting a cached fix would silently store a pin
 * from wherever the phone last looked, which is the failure PEC-29 describes with the geocoder
 * substituted for the cache.
 */
export const DEVICE_POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15_000,
  maximumAge: 0,
}

/**
 * Whether this browser exposes the Geolocation API at all.
 *
 * Synchronous, prompts nothing, and reads no permission state — the same contract as
 * `cameraSupported()`. It answers only "would `readDevicePosition()` fail with `unsupported`", so
 * a caller can decide whether to render the control rather than letting the user find out by
 * tapping it.
 */
export function geolocationSupported(
  geolocation: GeolocationLike | undefined = navigator.geolocation,
): boolean {
  return geolocation?.getCurrentPosition !== undefined
}

/** Requests one position fix at the point of use. Permission state stays browser-owned. */
export async function readDevicePosition(
  geolocation: GeolocationLike | undefined = navigator.geolocation,
): Promise<DevicePositionResult> {
  if (!geolocationSupported(geolocation) || geolocation === undefined) {
    return { ok: false, reason: 'unsupported' }
  }

  return new Promise<DevicePositionResult>((resolve) => {
    geolocation.getCurrentPosition(
      (position) =>
        resolve({
          ok: true,
          position: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracyMetres: position.coords.accuracy,
          },
        }),
      (error: unknown) => resolve({ ok: false, reason: positionFailureReason(error) }),
      DEVICE_POSITION_OPTIONS,
    )
  })
}

/**
 * Map a `GeolocationPositionError` to the three reasons the picker treats differently.
 *
 * `denied` is the one that changes behaviour rather than copy: §7.8 says a denied permission is
 * **never re-prompted in-flow**, so the caller retires the control on this reason and on no other.
 * The numeric codes are read rather than the `GeolocationPositionError` constructor, which jsdom
 * does not define and which no browser guarantees as a global.
 */
export function positionFailureReason(error: unknown): Exclude<GeolocationFailureReason, 'unsupported'> {
  const code =
    typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'number'
      ? error.code
      : undefined

  if (code === 1) return 'denied'
  if (code === 3) return 'timeout'
  return 'unavailable'
}
